import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * WhyThisNumberModal — 4-step explainer for a raw material's contribution to PCF.
 *
 * Renders in <20 seconds. Technical details (raw JSON, pedigree breakdown) live
 * inside an optional collapsible "Technical Details" section, never in the main flow.
 */
export default function WhyThisNumberModal({ open, onOpenChange, material, product }) {
  const [showTechnical, setShowTechnical] = useState(false);
  if (!material) return null;

  const qty = material.quantity_per_unit ?? material.quantity ?? 1;
  const ef = material.emission_factor ?? 0;
  const total = material.total_emissions ?? (qty * ef);
  const source = material.source || 'IEA / DEFRA / IPCC';
  const reliability = material.reliability || (ef > 0 ? 'High' : 'Medium');
  const regionMatch = material.region_match || (product?.country ? `EU standard (${product.country})` : 'EU standard');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="why-this-number-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-primary" />
            Why this number?
          </DialogTitle>
          <DialogDescription>
            A plain-language walkthrough of how <strong>{material.name}</strong>'s
            emissions were calculated.
          </DialogDescription>
        </DialogHeader>

        <ol className="space-y-4">
          <li className="rounded-lg border p-3" data-testid="why-step-1">
            <div className="text-xs font-semibold text-muted-foreground mb-1">Step 1 · Input</div>
            <div className="text-sm">Material: <strong>{material.name}</strong></div>
            <div className="text-sm">Quantity: <strong>{qty}</strong> per unit</div>
          </li>

          <li className="rounded-lg border p-3" data-testid="why-step-2">
            <div className="text-xs font-semibold text-muted-foreground mb-1">Step 2 · Applied Factor</div>
            <div className="text-sm">
              {source} factor: <strong className="font-mono">{ef}</strong> kg CO₂e / kg
            </div>
          </li>

          <li className="rounded-lg border p-3 bg-primary/5" data-testid="why-step-3">
            <div className="text-xs font-semibold text-muted-foreground mb-1">Step 3 · Result</div>
            <div className="text-sm">
              Total emissions: <strong className="font-mono text-primary text-base">{total}</strong> kg CO₂e
            </div>
          </li>

          <li className="rounded-lg border p-3" data-testid="why-step-4">
            <div className="text-xs font-semibold text-muted-foreground mb-1">Step 4 · Why this factor was chosen</div>
            <ul className="text-sm space-y-1 list-disc list-inside">
              <li>Source reliability: <strong>{reliability}</strong></li>
              <li>Region match: <strong>{regionMatch}</strong></li>
              <li>Data availability: <strong>best available dataset</strong></li>
            </ul>
          </li>
        </ol>

        <div className="pt-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowTechnical(v => !v)}
            data-testid="why-technical-toggle"
            className="text-xs"
          >
            {showTechnical ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
            Technical Details
          </Button>
          {showTechnical && (
            <pre
              data-testid="why-technical-body"
              className="text-[11px] bg-muted/50 rounded p-2 mt-2 max-h-40 overflow-auto font-mono"
            >
{JSON.stringify(material, null, 2)}
            </pre>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
